/**
 * Interface to Brooklyn Content Filter Sets API
 * @module services/content-filter-sets
 */
import _get from 'lodash/get'
import { fromJS } from 'immutable'
import request from '../request'
import { createAuthorization } from '../auth'

/**
 * Get Content Filter Sets by id from Brooklyn
 */
export async function getContentFilterSetById ({ auth, server, id }) {
  if (!id) {
    throw new Error('Please provide a content filter set id')
  }
  try {
    const res = await request
      .get(`${server}/v1/content-filter-sets/${id}`)
      .set('authorization', createAuthorization(auth))
      .set('accept', 'application/json')
    return fromJS(_get(res, 'body', []))
  } catch (error) {
    if (error.status === 404) {
      const err = new Error(`No content filter sets found for ${id}`)
      err.status = 404
      throw err
    }
    throw error
  }
}

/**
 * Get Content Filter Sets by name from Brooklyn
 */
export async function getContentFilterSetByName ({ auth, server, name }) {
  if (!name) {
    throw new Error('Please provide a content filter set name')
  }
  try {
    const res = await request
      .get(`${server}/v1/content-filter-sets?name=${name}`)
      .set('authorization', createAuthorization(auth))
      .set('accept', 'application/json')
    return fromJS(_get(res, 'body', []))
  } catch (error) {
    if (error.status === 404) {
      const err = new Error(`No content filter sets found for ${name}`)
      err.status = 404
      throw err
    }
    throw error
  }
}

/**
 * Get Content Filter Sets by Content Type and Content Id from Brooklyn
 */
export async function getContentFilterSetByContentTypeAndContentId ({
  auth, server, contentType, contentId,
}) {
  if (!contentType || !contentId) {
    throw new Error('Please provide a content filter set contentType and contentId')
  }
  try {
    const res = await request
      .get(`${server}/v1/content-filter-sets?contentType=${contentType}&contentId=${contentId}`)
      .set('authorization', createAuthorization(auth))
      .set('accept', 'application/json')
    return fromJS(_get(res, 'body', []))
  } catch (error) {
    if (error.status === 404) {
      const err = new Error(`No content filter sets found for ${contentType} and ${contentId}`)
      err.status = 404
      throw err
    }
    throw error
  }
}

/**
 * Get a list of Content Filter Sets from Brooklyn
 */
export async function getContentFilterSetList (options = {}) {
  const { auth, server } = options
  const res = await request
    .get(`${server}/v1/content-filter-sets/list`)
    .set('accept', 'application/json')
    .set('authorization', createAuthorization(auth))
  return fromJS(_get(res, 'body', {}))
}

/**
 * Create a Content Filter Set on Brooklyn
 */
export async function createContentFilterSet ({ auth, server, contentFilterSet }) {
  try {
    const res = await request
      .post(`${server}/v1/content-filter-sets`)
      .send(contentFilterSet)
      .set('authorization', createAuthorization(auth))
      .set('accept', 'application/json')
    return fromJS(_get(res, 'body', {}))
  } catch (error) {
    if (error.status === 409) {
      const err = new Error(`Unable to create a Content Filter Set with options: ${contentFilterSet}`)
      err.status = 409
      throw err
    }
    throw error
  }
}

/**
 * Delete a Content Filter Set by id from Brooklyn
 */
export async function deleteContentFilterSetById ({ auth, server, id }) {
  const res = await request
    .delete(`${server}/v1/content-filter-sets/${id}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(_get(res, 'body', {}))
}
